import { makeStyles } from '@mui/styles';
import Modal from 'apollo-react/components/Modal';
import TextField from 'apollo-react/components/TextField';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { verifyVirtualEntityExists } from '../../api/report-designer';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { createVirtualEntity } from '../actions/report-designer.actions';
import {
  REPORT_PUBLISH_VIRTUAL_ENTITY_ERROR_MESSAGES,
  UNSUPPORTED_SAVED_SEARCH_ELEMENTS_MESSAGE,
} from '../constants/validation.constants';
import { getReportModel, getVirtualEntityName } from '../selectors/report-designer.selectors';
import { validateVirtualEntiyNameOnBlur } from '../utils/report-designer.utils';
import { getContainsInvalidControlsForSavedSearch } from '../utils/validation.utils';

const useStyles = makeStyles({
  modal: {
    width: 650,
  },
  confirmModal: {
    '& [class*="MuiDialogContent-root"]': {
      paddingBottom: 5,
    },
  },
});

const PublishVirtualEntityModal = ({ handleClose, setIsLoading }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const inputFieldRef = useRef();
  const translateString = useTranslation();

  const reportModel = useSelector(getReportModel);
  const initialVirtualEntityName = useSelector(getVirtualEntityName);

  const [virtualEntityName, setVirtualEntityName] = useState(initialVirtualEntityName);
  const [error, setError] = useState({ hasError: false, errorText: '' });
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputFieldRef.current) {
        inputFieldRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleVirtualEntiyNameOnBlur = ({ target: { value } }) => {
    const validationError = validateVirtualEntiyNameOnBlur(value);
    validationError && setError(validationError);
  };

  const publushVirtualEntity = async () => {
    if (initialVirtualEntityName !== virtualEntityName) {
      const response = await verifyVirtualEntityExists(virtualEntityName);
      const isVirtualEntityExist = response.exists;

      if (isVirtualEntityExist) {
        const error = {
          hasError: true,
          errorText: REPORT_PUBLISH_VIRTUAL_ENTITY_ERROR_MESSAGES.UNIQUE,
        };

        setError(error);
        return;
      }
    }

    setIsLoading(true);
    await dispatch(createVirtualEntity({ virtualEntityName, handleModalClose: handleClose }));
    setIsLoading(false);
  };

  const handlePublishVirtualEntity = async () => {
    const containsInvalidControlsForSavedSearch = getContainsInvalidControlsForSavedSearch(
      reportModel
    );

    if (containsInvalidControlsForSavedSearch) {
      setConfirmModalOpen(true);
      return;
    }

    publushVirtualEntity();
  };

  const handleConfirmSaveVirtualEntity = () => {
    setConfirmModalOpen(false);
    publushVirtualEntity();
  };

  return (
    <>
      <Modal
        open
        variant="default"
        onClose={handleClose}
        title={translateString('Publish for Saved Searches')}
        buttonProps={[
          {
            label: translateString('Cancel'),
          },
          {
            label: translateString('Save'),
            onClick: handlePublishVirtualEntity,
            disabled: error.hasError,
          },
        ]}
        className={classes.modal}
        disableBackdropClick
      >
        <TextField
          label={translateString('Virtual Entity Name')}
          placeholder={translateString('Virtual Entity Name...')}
          name="virtualEntityName"
          value={virtualEntityName || ''}
          disabled={!!initialVirtualEntityName}
          onChange={(e) => setVirtualEntityName(e.target.value.toUpperCase())}
          onBlur={handleVirtualEntiyNameOnBlur}
          error={error.hasError}
          helperText={error.hasError ? translateString(error.errorText) : null}
          inputRef={inputFieldRef}
          fullWidth
          required
        />
      </Modal>
      <Modal
        open={confirmModalOpen}
        className={classes.confirmModal}
        variant="warning"
        onClose={() => setConfirmModalOpen(false)}
        title={translateString('Confirm Save')}
        buttonProps={[
          {
            label: translateString('Cancel'),
          },
          {
            label: translateString('Ok'),
            onClick: handleConfirmSaveVirtualEntity,
          },
        ]}
        disableBackdropClick
      >
        <p>{translateString(UNSUPPORTED_SAVED_SEARCH_ELEMENTS_MESSAGE)}</p>
      </Modal>
    </>
  );
};

export default PublishVirtualEntityModal;
